import React, { useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  Container, 
  Title, 
  Button, 
  Group, 
  Box,
  Text,
  Paper,
  Alert,
} from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import { BulkGenevaCreator } from "./components/BulkGenevaCreator";
import { MyUserDataContext } from "../../contexts";
import { CustomClaims } from "../../types";

export default function GenevaRequestBulkCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userContext = useContext(MyUserDataContext);

  const customClaims = (userContext.firebaseTokenResult?.claims?.customClaims as CustomClaims) || {};
  const canCreate = Boolean(customClaims.geneva_requests_create);
  
  // Get orgId and templateId from URL params or defaults
  const orgId = searchParams.get('orgId') || 'demo';
  const schemaTypeParam = searchParams.get('schemaType');
  const defaultTemplateId = schemaTypeParam === 'gwi' ? 'gwi' : 'default';
  const templateId = searchParams.get('templateId') || defaultTemplateId;

  if (!canCreate) {
    return (
      <Container size="xl" py="md">
        <Group justify="space-between" mb="xl">
          <Box>
            <Title order={2}>Bulk Create Geneva Requests</Title>
            <Text c="dimmed" size="sm">
              You do not have permission to create or edit Geneva requests.
            </Text>
          </Box>
          <Button
            variant="subtle"
            size="compact-md"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/geneva_requests')}
          >
            Back to Requests
          </Button>
        </Group>
        <Alert color="red" title="Not authorized">
          Contact an administrator if you believe you should have bulk creation access.
        </Alert>
      </Container>
    );
  }

  const handleSave = (requestId: string) => {
    // After successful creation, navigate to the bulk request details
    // For now, we'll navigate to the regular requests list
    navigate(`/geneva_requests`);
  };

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="xl">
        <Box>
          <Group gap="sm" align="center">
            <Button
              variant="subtle"
              size="compact-md"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate('/geneva_requests')}
            >
              Back to Requests
            </Button>
          </Group>
          <Title order={2} mt="sm">Bulk Create Geneva Requests</Title>
          <Text c="dimmed" size="sm">
            Upload a CSV or Excel file to create multiple Geneva requests at once
          </Text>
        </Box>
      </Group>

      <Paper shadow="sm" p="md" withBorder>
        <BulkGenevaCreator
          orgId={orgId}
          templateId={templateId}
          onSave={handleSave}
        />
      </Paper>
    </Container>
  );
}
